/**
 * Presentation logic for the live official-check column (verification pipeline v2).
 *
 * The backend writes `verification_views/{specialistId}`; this file only decides how a view,
 * or the API's compact `validation` block when there is no view, is shown. No Firestore here,
 * so it is trivially unit-testable.
 */

export interface EvidenceItem {
  source: string;
  status: string;
  checkedAt: string;
  reference?: string | null;
  detail?: string | null;
}

export type ResultStatus =
  | 'verified'
  | 'not_verified'
  | 'excluded'
  | 'manual_review'
  | 'inconclusive'
  | 'failed';

export interface VerificationViewDoc {
  specialistId: string;
  name?: string | null;
  /** Latest published result. Absent until the first result arrives. */
  status?: ResultStatus;
  /** State of the latest job: 'queued' from enqueue, then 'succeeded' | 'failed' with a result. */
  jobStatus?: 'queued' | 'succeeded' | 'failed';
  jobId?: string;
  generation?: number;
  lastCheckedAt?: string | null;
  nextCheckAt?: string | null;
  evidence?: EvidenceItem[];
  reason?: string | null;
  needsAdminReview?: boolean;
  updatedAt?: string;
}

/** The compact block `GET /verification/admin/pending` attaches when the backend flag is on. */
export interface ApiValidation {
  status?: ResultStatus;
  lastCheckedAt?: string | null;
  needsAdminReview?: boolean;
}

export type CheckPhase = 'not_started' | 'queued' | ResultStatus;

const RESULT_STATUSES: readonly string[] = [
  'verified',
  'not_verified',
  'excluded',
  'manual_review',
  'inconclusive',
  'failed',
];

export function phaseOf(
  view: VerificationViewDoc | null | undefined,
  apiValidation?: ApiValidation | null,
): CheckPhase {
  if (view) {
    // The job state outranks the stored result: a re-run queued after an old verdict must not
    // look final while it is in flight, and a failed re-run must not hide behind the old verdict.
    if (view.jobStatus === 'queued') return 'queued';
    if (view.jobStatus === 'failed') return 'failed';
    if (view.status && RESULT_STATUSES.includes(view.status)) return view.status;
    return 'not_started';
  }
  if (apiValidation?.status && RESULT_STATUSES.includes(apiValidation.status)) {
    return apiValidation.status;
  }
  return 'not_started';
}

const LABELS: Record<CheckPhase, string> = {
  not_started: 'Not started',
  queued: 'Queued',
  verified: 'Verified',
  manual_review: 'Manual review',
  inconclusive: 'Inconclusive',
  not_verified: 'Not verified',
  excluded: 'Excluded',
  failed: 'Check failed',
};

export function phaseLabel(phase: CheckPhase): string {
  return LABELS[phase];
}

export function phaseTone(phase: CheckPhase): 'neutral' | 'good' | 'warn' | 'bad' {
  switch (phase) {
    case 'verified':
      return 'good';
    case 'queued':
    case 'manual_review':
    case 'inconclusive':
      return 'warn';
    case 'not_verified':
    case 'excluded':
    case 'failed':
      return 'bad';
    default:
      return 'neutral';
  }
}

export interface AttentionSummary {
  needsReview: number;
  /** Up to five names of specialists flagged for review, so the banner can say who. */
  needsReviewNames: string[];
  failed: number;
  manualReview: number;
  total: number;
}

export function summarizeAttention(views: Iterable<VerificationViewDoc>): AttentionSummary {
  const out: AttentionSummary = { needsReview: 0, needsReviewNames: [], failed: 0, manualReview: 0, total: 0 };
  for (const v of views) {
    out.total += 1;
    if (v.needsAdminReview) {
      out.needsReview += 1;
      if (out.needsReviewNames.length < 5) out.needsReviewNames.push(v.name || v.specialistId);
    }
    const phase = phaseOf(v);
    if (phase === 'failed') out.failed += 1;
    // A row already counted as needing review is not counted again as manual review.
    if (phase === 'manual_review' && !v.needsAdminReview) out.manualReview += 1;
  }
  return out;
}

export function truncate(text: string | null | undefined, max: number): string {
  if (!text) return '';
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}
